<template>
  <div>
    <v-form ref="form" v-if="!isEnableNotificationSetting" class="px-4 py-2">
      <v-text-field
        v-model="localAlert.title"
        :label="$t('map.deviceAlerts.fields.title')"
        maxlength="20"
        counter="20"
        :disabled="editing"
        :rules="[rules.required]"
        class="text-field-required mb-3"
        data-cy="addTitle"
      ></v-text-field>
      <v-select
        v-model="localAlert.severity"
        :items="levels"
        :label="$t('map.deviceAlerts.fields.level')"
        class="mb-3"
        data-cy="addLevel"
      ></v-select>
      <v-row class="my-0" v-if="isBlueSkyOrAirAssure">
        <v-col cols="12" sm="6" class="py-0">
          <v-select
            v-model="localAlert.condition.field"
            :items="measurements"
            :disabled="editing"
            :label="$t('map.deviceAlerts.fields.sensor')"
            data-cy="addSensor"
            class="text-field-required mb-3"
            :rules="[rules.required]"
            @update:model-value="validateThreshold"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" class="py-0">
          <v-text-field
            ref="threshold"
            v-model="localAlert.condition.value"
            :label="$t('map.deviceAlerts.fields.value')"
            maxlength="10"
            :disabled="measurementSet"
            :rules="[rules.required, rules.validNum, rules.threshold, rules.precision]"
            @keypress="preventNonNumbers($event)"
            data-cy="addThreshold"
            class="text-field-required mb-3"
          ></v-text-field>
        </v-col>
      </v-row>
      <transition>
        <div v-if="helpText" class="help-text" data-cy="thresholdHelpText">
          <p>{{ helpText }}</p>
        </div>
      </transition>
      <v-row class="my-0">
        <v-col :cols="isDustTrak ? '8' : '12'" class="py-0">
          <v-select
            v-if="isBlueSkyOrAirAssure"
            v-model="localAlert.condition.operator"
            :items="operators"
            :disabled="editing"
            :label="$t('map.deviceAlerts.fields.condition')"
            @update:model-value="validateThreshold"
            class="mb-3"
            data-cy="addCondition"
          ></v-select>
          <v-tooltip
            :disabled="!oneCondition || editing"
            location="bottom"
            offset="-16"
            max-width="460"
            min-width="460"
            v-else
          >
            <template v-slot:activator="{ props }">
              <span v-bind="props">
                <v-select
                  v-model="localAlert.condition.field"
                  :items="conditions"
                  :disabled="editing || oneCondition"
                  :label="$t('map.deviceAlerts.fields.condition')"
                  @update:model-value="setConditionFields"
                  class="mb-3"
                  data-cy="addCondition"
                ></v-select>
              </span>
            </template>
            <span data-cy="disabledConditions">{{
              $t('map.deviceAlerts.fields.disabledConditions')
            }}</span>
          </v-tooltip>
        </v-col>
        <v-col v-if="isDustTrak" cols="4" class="py-0">
          <v-tooltip
            location="bottom"
            offset="-16"
            max-width="460"
            min-width="460"
            :disabled="!showPMChannelTooltip"
            content-class="pm-channel-tooltip"
          >
            <template v-slot:activator="{ props }">
              <span v-bind="props">
                <v-select
                  v-model="localAlert.condition.pmChannel"
                  :items="pmChannels"
                  :disabled="editing || emptyPMChannels || onePMChannel"
                  :label="$t('map.deviceAlerts.fields.pm_channel')"
                  class="mb-3"
                  data-cy="addPMChannel"
                />
              </span>
            </template>
            <span v-if="onePMChannel" data-cy="oneAvailablePMChannel">
              {{ $t('map.deviceAlerts.fields.oneAvailablePMChannel') }}
            </span>
            <span v-else data-cy="disabledPMChannels">
              {{ $t('map.deviceAlerts.fields.disabledPMChannels') }}
            </span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-tooltip
        :disabled="isBlueSkyOrAirAssure"
        location="bottom"
        offset="-16"
        max-width="460"
        min-width="460"
      >
        <template v-slot:activator="{ props }">
          <span v-bind="props">
            <v-select
              v-bind="props"
              v-model="localAlert.frequencyType"
              :items="frequencyType"
              :label="$t('map.deviceAlerts.fields.frequency')"
              class="mb-3"
              data-cy="frequency"
              :disabled="isDustTrak || isSmartBridge"
            ></v-select>
          </span>
        </template>
        <span data-cy="disabledFrequency">{{
          $t('map.deviceAlerts.fields.disabledFrequency')
        }}</span>
      </v-tooltip>

      <div class="pb-5" v-if="isNotificationEnabled">
        {{ $t('map.deviceAlerts.enableNotificationsHelp') }}
      </div>
      <v-row align="center" class="my-0 mb-3">
        <v-col cols="12" sm="6" class="py-0">
          <v-checkbox
            color="primary"
            v-model="isSendNotification"
            @update:model-value="removeSelection"
            :label="$t('map.deviceAlerts.fields.sendNotifications')"
            :disabled="
              (isNotificationEnabled && !localAlert.sendEmail && !localAlert.sendSms) ||
              enableNotificationBtn
            "
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" class="py-0">
          <v-btn
            v-if="enableNotificationBtn"
            variant="tonal"
            class="text-primary mobile-menu"
            @click="toggleNotification"
          >
            {{ $t('map.deviceAlerts.fields.enableNotifications') }}
          </v-btn>
        </v-col>
      </v-row>
      <div v-if="isSendNotification" class="mb-3">
        <v-label>{{ $t('map.deviceAlerts.fields.deliverTo') }}</v-label>
        <v-checkbox
          class="zero-height-v-messages"
          color="primary"
          v-model="localAlert.sendSms"
          :label="getSmsLabel"
          :disabled="disableSms"
          hide-details
        />
        <v-btn
          v-if="!enableNotificationBtn && disableSms"
          variant="tonal"
          class="text-primary mobile-menu"
          @click="toggleNotification"
        >
          {{ $t('map.deviceAlerts.fields.enableNotifications') }}
        </v-btn>
        <v-checkbox
          class="zero-height-v-messages"
          color="primary"
          v-model="localAlert.sendEmail"
          :label="getEmailLabel"
          :disabled="disableEmail"
          hide-details
        />
        <v-btn
          v-if="!enableNotificationBtn && disableEmail"
          variant="tonal"
          class="text-primary mobile-menu"
          @click="toggleNotification"
        >
          {{ $t('map.deviceAlerts.fields.enableNotifications') }}
        </v-btn>
      </div>

      <v-text-field
        v-model="localAlert.userMessage"
        :label="$t('map.deviceAlerts.fields.message')"
        maxlength="25"
        counter="25"
        data-cy="addCustomMessage"
      />
      <v-card-actions>
        <div class="hint-text">
          {{ $t('validation.requiredHelp') }}
        </div>
      </v-card-actions>
    </v-form>
    <FusionNotificationSettings
      v-if="isEnableNotificationSetting"
      :isSmallDisclaimerTitle="true"
      :showMobileDoneButton="true"
      class="enable-notifications"
      @onSave="onNotificationSelect"
      @close="toggleNotification"
      @onDisclaimerAgree="onDisclaimerAgree"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, toRaw, type PropType } from 'vue'
import type { VTextField } from 'vuetify/lib/components'
import { getAuthInstance } from '@fusion/auth-v3'
import { FusionStores } from '@fusion/saas-components-v3'
import { ProductFamily } from '@/services/map'
import { prefixToNewField } from '@/services/device-alerts'
import type { ConditionValue } from '@/types/alerts'
import type { DeviceModelField } from '@/types/devices'
import type { Phone, SelectItem, TriggerDraft } from '../dialogs/DeviceAlertsDialog.vue'

export default defineComponent({
  name: 'UpdateAlert',

  props: {
    modelValue: {
      type: Object as PropType<TriggerDraft>,
      required: true
    },
    rules: {
      type: Object as PropType<Record<string, (v: ConditionValue) => boolean | string>>,
      required: true
    },
    phone: {
      type: Object as PropType<Phone>,
      default: undefined
    },
    isEditingAlert: {
      type: Boolean,
      default: false
    },
    isSmsDisabled: {
      type: Boolean,
      default: false
    },
    isEmailDisabled: {
      type: Boolean,
      default: false
    },
    isNotificationEnabled: {
      type: Boolean,
      default: false
    },
    deviceId: {
      type: String,
      default: ''
    },
    measurements: {
      type: Array as PropType<SelectItem[]>,
      default: (): SelectItem[] => []
    },
    helpText: {
      type: String,
      default: ''
    },
    modelProductFamily: {
      type: String,
      default: ''
    },
    alertables: {
      type: Array as PropType<DeviceModelField[]>,
      default: (): DeviceModelField[] => []
    }
  },

  emits: ['toggleNotification', 'onDisclaimerAgree', 'update:modelValue'],

  setup() {
    const userProfileStore = FusionStores.userProfile(getAuthInstance())
    return { userProfileStore }
  },

  data() {
    return {
      operators: [
        { title: this.$t('map.deviceAlerts.fields.operators.lt'), value: 'lt' },
        { title: this.$t('map.deviceAlerts.fields.operators.lte'), value: 'lte' },
        { title: this.$t('map.deviceAlerts.fields.operators.eq'), value: 'eq' },
        { title: this.$t('map.deviceAlerts.fields.operators.gte'), value: 'gte' },
        { title: this.$t('map.deviceAlerts.fields.operators.gt'), value: 'gt' }
      ] as SelectItem[],
      conditions: [] as SelectItem[],
      pmChannels: [] as SelectItem[],
      levels: [
        { title: this.$t('map.deviceAlerts.fields.levels.standard'), value: 'standard' },
        { title: this.$t('map.deviceAlerts.fields.levels.warning'), value: 'warning' },
        { title: this.$t('map.deviceAlerts.fields.levels.critical'), value: 'critical' }
      ] as SelectItem[],
      frequencyType: [
        {
          title: this.$t('map.deviceAlerts.fields.frequencyTypes.onceCrossed'),
          value: 'onceCrossed'
        },
        {
          title: this.$t('map.deviceAlerts.fields.frequencyTypes.everyCrossed'),
          value: 'everyCrossed'
        }
      ] as SelectItem[],
      isSendNotification: false,
      mobileNumber: 'not added',
      primaryEmail: 'not added',
      isEnableNotificationSetting: false,
      isNotificationOptionSelected: false,
      disableSms: this.isSmsDisabled,
      disableEmail: this.isEmailDisabled,
      enableNotificationBtn: this.isNotificationEnabled,
      productFamilies: ProductFamily,
      localAlert: structuredClone(toRaw(this.modelValue)) as TriggerDraft
    }
  },

  watch: {
    modelValue: {
      deep: true,
      handler(newVal: TriggerDraft) {
        if (JSON.stringify(toRaw(newVal)) !== JSON.stringify(toRaw(this.localAlert))) {
          this.localAlert = structuredClone(toRaw(newVal))
        }
      }
    },
    localAlert: {
      deep: true,
      handler(newVal: TriggerDraft) {
        this.$emit('update:modelValue', structuredClone(toRaw(newVal)))
      }
    },
    isSmsDisabled(newVal: boolean) {
      this.disableSms = newVal
    },
    isEmailDisabled(newVal: boolean) {
      this.disableEmail = newVal
    },
    isNotificationEnabled(newVal: boolean) {
      this.enableNotificationBtn = newVal
    }
  },

  computed: {
    editing(): boolean {
      return !!this.localAlert.id
    },
    getSmsLabel(): string {
      const mobilePhoneText = this.$t('map.deviceAlerts.fields.mobilePhone')
      const pendingText = this.$t('userProfile.notificationSettings.pending')
      const requiresOptinText = this.$t('map.deviceAlerts.requiresOptIn')
      let label = `${mobilePhoneText}`
      if (this.phone?.isPrimary && this.phone?.smsNotificationsPending) {
        label += ` (${this.mobileNumber}) (${pendingText})`
      } else if (
        this.phone?.isPrimary &&
        !this.phone?.smsNotificationsPending &&
        !this.phone?.allowSmsNotifications
      ) {
        label += ` (${requiresOptinText})`
      } else {
        label += ` (${this.mobileNumber})`
      }
      return label
    },
    getEmailLabel(): string {
      let label = `${this.$t('map.deviceAlerts.fields.primaryEmail')}`
      if (!this.userProfileStore.user?.allowEmailNotifications) {
        label += ` (${this.$t('map.deviceAlerts.requiresOptIn')})`
      } else {
        label += ` (${this.primaryEmail})`
      }
      return label
    },
    measurementSet(): boolean {
      return this.localAlert.condition.field === ''
    },
    oneCondition(): boolean {
      return this.conditions.length === 1
    },
    onePMChannel(): boolean {
      return !!(this.pmChannels.length === 1 && this.pmChannels[0].value)
    },
    emptyPMChannels(): boolean {
      return !!(
        !this.pmChannels.length ||
        (this.pmChannels.length === 1 && !this.pmChannels[0].value)
      )
    },
    showPMChannelTooltip(): boolean {
      return this.onePMChannel || this.emptyPMChannels
    },
    isBlueSkyOrAirAssure(): boolean {
      return (
        this.modelProductFamily === this.productFamilies.BLUESKY ||
        this.modelProductFamily === this.productFamilies.AIRASSURE
      )
    },
    isDustTrak(): boolean {
      return this.modelProductFamily === this.productFamilies.DUSTTRAK
    },
    isSmartBridge(): boolean {
      return this.modelProductFamily === this.productFamilies.SMARTBRIDGE
    }
  },

  async mounted() {
    this.primaryEmail = this.userProfileStore.user?.email ?? 'not added'
    if (this.phone?.number) {
      this.mobileNumber = this.phone.number
    }
    if (this.isEditingAlert) {
      this.isSendNotification = this.localAlert.sendEmail || this.localAlert.sendSms
    }
    this.setConditions()
    this.setPMChannels()
  },

  methods: {
    validateThreshold() {
      ;(this.$refs.threshold as VTextField).validate()
    },
    preventNonNumbers(event: KeyboardEvent) {
      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '-', '.']
      if (allowedKeys.includes(event.key)) return
      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    },
    toggleNotification() {
      this.isEnableNotificationSetting = !this.isEnableNotificationSetting
      this.isNotificationOptionSelected = !this.isNotificationOptionSelected
      this.enableNotificationBtn = this.disableSms && this.disableEmail
      this.$emit('toggleNotification')
    },
    onNotificationSelect(e: { smsEnabled: boolean; emailEnabled: boolean }) {
      this.disableSms = !e.smsEnabled
      this.disableEmail = !e.emailEnabled
    },
    onDisclaimerAgree() {
      this.$emit('onDisclaimerAgree')
    },
    removeSelection() {
      if (!this.isSendNotification) {
        this.localAlert.sendSms = false
        this.localAlert.sendEmail = false
      }
    },
    // Returns the conditions array with the new condition inserted before the matched item
    addNewCondition(
      conditions: DeviceModelField[],
      key: keyof DeviceModelField,
      value: string,
      name: string
    ): DeviceModelField[] {
      if (conditions?.length) {
        const newConditionIndex = this.getIndexPositionWhichContainsValue(conditions, key, value)
        if (newConditionIndex !== -1) {
          return [
            ...conditions.slice(0, newConditionIndex),
            { name } as DeviceModelField,
            ...conditions.slice(newConditionIndex)
          ]
        }
      }
      return conditions
    },
    addNewConditions(conditions: DeviceModelField[]): DeviceModelField[] {
      if (conditions?.length) {
        let newConditions = [...conditions]
        for (const [prefix, newFieldName] of Object.entries(prefixToNewField)) {
          newConditions = this.addNewCondition(newConditions, 'name', prefix, newFieldName)
        }
        return newConditions
      }
      return conditions
    },
    removeConditions(conditions: DeviceModelField[]): DeviceModelField[] {
      if (conditions?.length) {
        return conditions.filter((condition) => {
          let remove = false
          for (const [prefix, newFieldName] of Object.entries(prefixToNewField)) {
            const { name } = condition
            if (name.includes(prefix) && name !== newFieldName) {
              remove = true
              break
            }
          }
          return !remove
        })
      }
      return conditions
    },
    getFormattedCondition(value: string, locale: string): SelectItem {
      return {
        title: this.$t(`${locale}.${value}`),
        value
      }
    },
    // For DustTrak alerts, conditions dropdown sets "fields" instead of "operators"
    setConditions() {
      const locale = `map.deviceAlerts.fields.${this.modelProductFamily}Conditions`
      let conditions = [...this.alertables]
      conditions = this.addNewConditions(conditions)
      conditions = this.removeConditions(conditions)
      this.conditions = conditions.map((condition) =>
        this.getFormattedCondition(condition?.name, locale)
      )
      // If only one condition exists, set as default choice
      if (this.oneCondition) {
        this.localAlert.condition.field = this.conditions[0].value as string
        this.setConditionFields()
      }
    },
    getConditionPMChannels(conditionPrefix: string): SelectItem[] {
      const locale = `map.deviceAlerts.fields.${this.modelProductFamily}Conditions.pmChannels`
      return this.alertables
        .filter((alert) => alert.name.includes(conditionPrefix))
        .map((alert) => {
          const measurement = alert.name.replace(conditionPrefix, '')
          return this.getFormattedCondition(measurement, locale)
        })
    },
    setPMChannels() {
      let populatePMChannels = false
      for (const [prefix, newFieldName] of Object.entries(prefixToNewField)) {
        if (this.localAlert.condition.field === newFieldName) {
          this.pmChannels = this.getConditionPMChannels(prefix)
          populatePMChannels = true
          break
        }
      }
      if (!populatePMChannels) {
        this.pmChannels = [{ title: this.$t('map.deviceAlerts.notApplicable'), value: '' }]
        this.localAlert.condition.pmChannel = ''
      }
      if (this.pmChannels.length && !this.localAlert.condition.pmChannel) {
        this.localAlert.condition.pmChannel = this.pmChannels[0].value as string
      }
    },
    // For state alerts, operator/type/value are hardcoded (users cannot modify them)
    setConditionFields() {
      this.setPMChannels()
      if (this.localAlert.condition.field === 'battery_remaining') {
        this.localAlert.condition.operator = 'lte'
        this.localAlert.condition.value = 20
      } else {
        this.localAlert.condition.type = 'bool'
        this.localAlert.condition.operator = 'eq'
        this.localAlert.condition.value = true
      }
    },
    getIndexPositionWhichContainsValue(
      array: DeviceModelField[],
      key: keyof DeviceModelField,
      value: string
    ): number {
      if (array?.length) {
        return array.findIndex((item) => String(item[key]).includes(value))
      }
      return -1
    }
  }
})
</script>

<style lang="scss">
.zero-height-v-messages {
  .v-messages {
    display: none;
  }
}
.v-select {
  pointer-events: initial;
}
</style>

<style lang="scss" scoped>
.hint-text {
  font-size: 12px;
  opacity: 0.76;
  color: red;
}
.help-text {
  display: flex;
  align-items: flex-start;
  font-size: 0.8125rem;
  line-height: 1.375;
  padding: 8px 12px;
  border: 1px solid var(--tsi-color--main-primary);
  border-top-width: 4px;
  border-radius: 4px;
  margin-top: -8px;
  margin-bottom: 32px;
  .v-icon {
    margin-right: 8px;
  }
  p {
    margin: 0;
  }
}
.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
}
.v-overlay__content {
  transform: translate(0px, -30px);
}
.pm-channel-tooltip {
  transform: translate(-158px, -30px);
}
.v-checkbox {
  margin-left: -12px;
}
.v-card-actions {
  padding: 0;
}
.enable-notifications :deep(.v-card-actions) {
  padding: 0 16px;
}
</style>
